<template>
  <div class="reg">
    <div v-if="submitted" key="done" class="reg__done">
      <v-icon color="primary" size="64">mdi-email-check-outline</v-icon>
      <h2 class="reg__title mt-4">{{ $t("login.verifyEmailHeading") }}</h2>
      <p class="reg__subtitle">
        {{ $t("login.verifyEmailText", { email: submittedEmail }) }}
      </p>
    </div>

    <v-form
      v-else-if="page === 1"
      key="pageOne"
      ref="pageOne"
      lazy-validation
      @submit.prevent="goToPassword"
    >
      <h2 class="reg__title">{{ $t("login.registerTitle") }}</h2>
      <p class="reg__subtitle">{{ $t("login.registerSubtitle") }}</p>

      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="regForm.name"
            prepend-icon="person"
            :label="$t('login.name')"
            :rules="requiredRule"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="regForm.email"
            prepend-icon="mail"
            :label="$t('login.email')"
            type="email"
            :rules="emailRules"
          />
        </v-col>
      </v-row>
      <v-row dense class="mt-4">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="clientData.name"
            prepend-icon="business"
            :label="$t('login.companyName')"
            :rules="requiredRule"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="8">
          <v-text-field
            v-model="clientData.companyData.address.street"
            prepend-icon="place"
            :label="$t('login.street')"
            :rules="requiredRule"
          />
        </v-col>
        <v-col cols="12" md="4" class="reg__indent-sm">
          <v-text-field
            v-model="clientData.companyData.address.streetNumber"
            :label="$t('login.number')"
            :rules="requiredRule"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="6" class="reg__indent">
          <v-text-field
            v-model="clientData.companyData.address.postalCode"
            :label="$t('login.zip')"
            type="number"
            :rules="plzRules"
          />
        </v-col>
        <v-col cols="12" md="6" class="reg__indent-sm">
          <v-text-field
            v-model="clientData.companyData.address.city"
            :label="$t('login.city')"
            :rules="requiredRule"
          />
        </v-col>
      </v-row>

      <div class="reg__actions">
        <v-btn color="primary" min-width="200" type="submit">
          {{ $t("login.next") }}
        </v-btn>
      </div>
    </v-form>

    <v-form v-else key="pageTwo" ref="form" lazy-validation @submit.prevent="register()">
      <h2 class="reg__title">{{ $t("login.passwordStepTitle") }}</h2>
      <p class="reg__subtitle">{{ $t("login.passwordStepSubtitle") }}</p>

      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="regForm.password"
            id="password"
            prepend-icon="lock"
            :label="$t('login.password')"
            :rules="passRules"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            autocomplete="new-password"
            @click:append="showPassword = !showPassword"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="regForm.password1"
            id="password1"
            prepend-icon="lock"
            :label="$t('login.enterPassword')"
            :rules="passRules"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            autocomplete="new-password"
            @click:append="showPassword = !showPassword"
          />
          <p v-if="regForm.registerPasswWarning" class="error--text mb-0">
            {{ $t("login.passwordsNotIdentical") }}
          </p>
        </v-col>
      </v-row>

      <v-checkbox :rules="requiredRule" v-model="checkbox" dense class="mt-6">
        <template v-slot:label>
          <div>
            {{ $t("login.clickHere") }}
            <a href="https://www.safetyplanner.de/datenschutz.html" target="_blank" @click.stop>
              {{ $t("login.privacy") }}
            </a>
            {{ $t("login.and") }}
            <a href="https://www.safetyplanner.de/agb.html" target="_blank" @click.stop>
              {{ $t("login.agreement") }}
            </a>
            {{ $t("login.accept") }}
          </div>
        </template>
      </v-checkbox>
      <v-checkbox :rules="requiredRule" v-model="legalNotice" dense>
        <template v-slot:label>
          {{ $t("login.legalNotice") }}
        </template>
      </v-checkbox>
      <div v-if="turnstileSiteKey" ref="turnstile" class="my-3"></div>

      <div class="reg__actions">
        <v-btn text color="primary" class="mr-3" :disabled="loading" @click="goBack">
          {{ $t("login.back") }}
        </v-btn>
        <v-btn
          color="primary"
          min-width="200"
          :loading="loading"
          :disabled="!!turnstileSiteKey && !captchaToken"
          type="submit"
        >
          {{ $t("login.registerNow") }}
        </v-btn>
      </div>
    </v-form>
  </div>
</template>
<script>
export default {
  data() {
    return {
      loading: false,
      checkbox: false,
      legalNotice: false,
      showPassword: false,
      submitted: false,
      submittedEmail: "",
      page: 1,
      captchaToken: "",
      turnstileWidgetId: null,
      plzRules: [
        (v) => !!v || this.$t("general.zipRequired"),
        (v) =>
          (v && v.length <= 5) ||
          this.$t("general.zipMaxLength"),
      ],
      emailRules: [
        (v) => !!v || this.$t("general.emailRequired"),
        (v) => /.+@.+/.test(v) || this.$t("general.emailInvalid"),
      ],
      passRules: [
        (v) => !!v || this.$t("general.passwordRequired"),
        (v) =>
          (!!v && v.length >= 8) ||
          this.$t("general.passwordMinLength"),
      ],
      requiredRule: [(v) => !!v || this.$t("general.fieldNeedsToBeFilled")],
      regForm: {
        name: "",
        email: "",
        password: "",
        password1: "",
        registerPasswWarning: false,
      },
      clientData: {
        name: "",
        license: "sp-free",
        companyData: {
          numberOfEmployees: 10,
          address: {
            postalCode: null,
            streetNumber: null,
            street: "",
            city: "",
            country: "DE",
          },
        },
      },
    };
  },
  computed: {
    turnstileSiteKey() {
      return this.$store.getters.turnstileSiteKey;
    },
  },
  watch: {
    submitted: {
      immediate: true,
      handler(value) {
        this.$emit("step", value ? 2 : 1);
      },
    },
  },
  methods: {
    goBack() {
      if (window.turnstile && this.turnstileWidgetId !== null) {
        try {
          window.turnstile.remove(this.turnstileWidgetId);
        } catch (e) {
          this.turnstileWidgetId = null;
        }
      }
      this.turnstileWidgetId = null;
      this.captchaToken = "";
      this.page = 1;
    },
    goToPassword() {
      if (!this.$refs.pageOne.validate()) return;
      this.page = 2;
      if (this.turnstileSiteKey) {
        this.$nextTick(() => this.loadTurnstile());
      }
    },
    loadTurnstile() {
      const render = () => {
        if (window.turnstile && this.$refs.turnstile) {
          this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstile, {
            sitekey: this.turnstileSiteKey,
            callback: (token) => {
              this.captchaToken = token;
            },
            "expired-callback": () => {
              this.captchaToken = "";
            },
            "error-callback": () => {
              this.captchaToken = "";
            },
          });
        }
      };
      if (window.turnstile) {
        render();
        return;
      }
      const existing = document.getElementById("cf-turnstile-script");
      if (existing) {
        existing.addEventListener("load", render);
        return;
      }
      const s = document.createElement("script");
      s.id = "cf-turnstile-script";
      s.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.defer = true;
      s.onload = render;
      document.head.appendChild(s);
    },
    resetCaptcha() {
      this.captchaToken = "";
      if (window.turnstile && this.turnstileWidgetId !== null) {
        window.turnstile.reset(this.turnstileWidgetId);
      }
    },
    register() {
      if (this.loading) return;
      this.loading = true;

      if (this.$refs.form.validate()) {
        this.regForm.registerPasswWarning = false;

        if (this.regForm.password !== this.regForm.password1) {
          this.regForm.registerPasswWarning = true;
          this.loading = false;
        } else {
          this.$store.dispatch("getAllTicketsFromStorage").then((r) => {
            this.regForm.email = this.regForm.email.trim().toLowerCase();
            this.regForm.password = this.regForm.password.trim();
            this.regForm.password1 = this.regForm.password1.trim();
            this.$store
              .dispatch("register", {
                user: this.regForm,
                client: this.clientData,
                tickets: r.tickets,
                results: r.values,
                captchaToken: this.captchaToken,
              })
              .then((ok) => {
                this.loading = false;
                if (ok) {
                  this.submittedEmail = this.regForm.email;
                  this.submitted = true;
                } else {
                  this.resetCaptcha();
                }
              });
          });
        }
      } else {
        this.loading = false;
      }
    },
  },
};
</script>
<style scoped>
.reg__title {
  font-family: Roboto, sans-serif;
  font-weight: 600;
  font-size: 28px;
  line-height: 100%;
  letter-spacing: 0;
  color: #1a5561;
  margin: 0 0 16px;
}
.reg__subtitle {
  font-family: Roboto, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 40px;
}
.reg__actions {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 36px;
}
.reg__done {
  text-align: center;
  padding: 24px 0;
}
.reg__done .reg__subtitle {
  margin-bottom: 0;
}
@media (min-width: 960px) {
  .reg__indent {
    padding-left: 37px !important;
  }
  .reg__indent-sm {
    padding-left: 24px !important;
  }
}
</style>
